import React, { useCallback, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import ConfirmDialog from "../components/ConfirmDialog";
import { isBlank, notBlank } from "../util/ext";
import Paper from "@mui/material/Paper";
import { useConfirm } from "../common/use-confirm";
import { append, includes, pluck, without, filter, prop, sortBy } from "ramda";
import Grid from "@mui/material/Grid";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import Button from "@mui/material/Button";
import { useRoles, useUpdateUserOrganizationRoles } from "./roleServices";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

const useStyles = makeStyles(theme => ({
  form: {
    marginTop: theme.spacing(3),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

const EditUserOrganizationDialog = props => {
  const { open, onCancel, onOk, onError, user, organization } = props;
  const classes = useStyles();

  //-- Manage the confirm dialog
  const confirmHandler = useConfirm();
  const { showConfirm } = confirmHandler;

  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);

  const roles = useRoles();
  useEffect(() => {
    if (roles.isSuccess && notBlank(organization)) {
      setAvailableRoles(
        sortBy(
          prop("roleName"),
          filter(r => r.roleType === "USER", roles.data),
        ),
      );
      setSelectedRoles(organization.userOrganizationRoles);
    }
  }, [roles.isSuccess, roles.data, organization]);

  const updateUserOrganizationRoles = useUpdateUserOrganizationRoles();
  const handleApply = useCallback(async () => {
    const roleList = pluck("roleName", selectedRoles);
    try {
      await updateUserOrganizationRoles.mutateAsync({
        resourceIds: {
          userId: user.userId,
          organizationId: organization.organizationId,
        },
        data: roleList,
      });
      setSelectedRoles([]);
      onOk();
    } catch (err) {
      onError(err);
    }
  }, [
    selectedRoles,
    user,
    organization,
    updateUserOrganizationRoles,
    onOk,
    onError,
  ]);

  const handleClose = useCallback(() => {
    if (isBlank(selectedRoles)) {
      onCancel();
    } else {
      showConfirm(
        "Discard Changes?",
        "You have selected roles without applying.  Do you wish to discard these changes?",
        {
          onConfirm: () => {
            onCancel();
          },
        },
      );
    }
  }, [selectedRoles, showConfirm, onCancel]);

  const handleRoleSelected = role => event => {
    if (event.target.checked) {
      setSelectedRoles(prev => append(role, prev));
    } else {
      setSelectedRoles(prev => without([role], selectedRoles));
    }
  };

  return (
    <Dialog
      fullWidth
      open={open}
      onClose={handleClose}
      aria-labelledby="edit-organization-dialog-title"
    >
      <DialogTitle id="edit-organization-dialog-title">
        Edit User Organization Roles
      </DialogTitle>
      <DialogContent>
        <ConfirmDialog handler={confirmHandler} />
        <Paper>
          <Box className={classes.form}>
            <Grid spacing={2} container>
              {availableRoles.map(role => (
                <Grid key={role.roleId} item md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includes(role, selectedRoles)}
                        onChange={handleRoleSelected(role)}
                        name={role.roleName}
                        color="primary"
                      />
                    }
                    label={role.roleName}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserOrganizationDialog;
